import React, { useCallback } from "react";

import { useIntl } from "react-intl";

import { DynamicTableStateless } from "@atlaskit/dynamic-table";
import { Stack, Text, xcss, Box } from "@atlaskit/primitives-next";

import { EmptySearchVector } from "../assets";

import { SORT_DIRECTION, PAGINATION_DIRECTION } from "./constants";
import { FastshiftTablePagination } from "./fastshift-table-pagination";
import { FastshiftTableSearch } from "./fastshift-table-search";
import { FastshiftTableWrapper } from "./fastshift-table-wrapper";
import { useFastshiftTableControls, useFastshiftTableAnalytics } from "./hooks";
import { messages } from "./messages";
import { FastshiftTableProps, SortBy } from "./types";

const emptyTableContainerStyles = xcss({
    height: "200px",
    width: "100%",
    padding: "space.400"
});

const emptyTableIconContainer = xcss({
    paddingBlock: "space.100"
});

export const FastshiftTable: React.FC<FastshiftTableProps> = ({
    enableSearch = false,
    summaryText,
    isTableLoading,
    isTableError,
    errorComponent,
    head,
    rows,
    controls,
    id = "fastshift-table",
    testId = "fastshift-table",
    analyticsSource,
    analyticsIdentity
}) => {
    const intl = useIntl();
    const { pagination, sorting, search } = useFastshiftTableControls(controls);

    const {
        sendSearchUIEvent,
        sendNavigationUIEvent,
        sendPageSizeChangeUIEvent,
        sendSortUIEvent
    } = useFastshiftTableAnalytics(analyticsIdentity, analyticsSource);

    // Enhanced sort handler that triggers analytics
    const handleSort = useCallback(
        (key: SortBy) => {
            const nextOrder =
                sorting.sortBy === key &&
                sorting.sortDirection === SORT_DIRECTION.ASC
                    ? SORT_DIRECTION.DESC
                    : SORT_DIRECTION.ASC;
            sorting.onSort(key, nextOrder);
            sendSortUIEvent(key, nextOrder);
        },
        [sorting, sendSortUIEvent]
    );

    // Enhanced search handler that triggers analytics
    const enhancedSearch = {
        ...search,
        onSetSearchTerm: useCallback(
            (searchTerm: string) => {
                search.onSetSearchTerm(searchTerm);
                sendSearchUIEvent();
            },
            [search, sendSearchUIEvent]
        )
    };

    // Enhanced pagination handlers that trigger analytics
    const enhancedPagination = {
        ...pagination,
        onClickNext: useCallback(() => {
            pagination.onClickNext();
            sendNavigationUIEvent(
                PAGINATION_DIRECTION.NEXT,
                pagination.totalPages
            );
        }, [pagination, sendNavigationUIEvent]),
        onClickPrevious: useCallback(() => {
            pagination.onClickPrevious();
            sendNavigationUIEvent(
                PAGINATION_DIRECTION.PREVIOUS,
                pagination.totalPages
            );
        }, [pagination, sendNavigationUIEvent]),
        onSizeChange: useCallback(
            (size: number) => {
                pagination.onSizeChange(size);
                sendPageSizeChangeUIEvent(size);
            },
            [pagination, sendPageSizeChangeUIEvent]
        )
    };

    if (isTableError) {
        return errorComponent;
    }

    return (
        <Stack space="space.200">
            {enableSearch && (
                <FastshiftTableSearch
                    id={`${id}-search`}
                    search={enhancedSearch}
                    testId={`${testId}-search`}
                />
            )}
            {summaryText && <Text size="medium">{summaryText}</Text>}
            <FastshiftTableWrapper
                id={`${id}-wrapper`}
                testId={`${testId}-wrapper`}
            >
                <DynamicTableStateless
                    testId={`${testId}-table`}
                    head={head}
                    rows={rows}
                    isLoading={isTableLoading}
                    isFixedSize
                    onSort={(data) => handleSort(data.key as SortBy)}
                    sortOrder={sorting.sortDirection ?? SORT_DIRECTION.ASC}
                    sortKey={sorting.sortBy}
                    emptyView={
                        <Stack
                            alignBlock="center"
                            alignInline="center"
                            xcss={emptyTableContainerStyles}
                            space="space.300"
                        >
                            <Box xcss={emptyTableIconContainer}>
                                <EmptySearchVector />
                            </Box>
                            <Text>
                                {intl.formatMessage(messages.emptyTableMessage)}
                            </Text>
                        </Stack>
                    }
                />
                <FastshiftTablePagination pagination={enhancedPagination} />
            </FastshiftTableWrapper>
        </Stack>
    );
};
